/**
 * Hook React Query para Voice Commerce M4 — WhatsApp Quick Actions.
 *
 * Mutation que invoca al edge function `wa-quick-actions` con discriminated
 * action union (send_location | send_product | send_text).
 */

import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';

export type WaQuickActionInput =
  | { action: 'send_location'; customer_phone: string; customer_name?: string; crm_call_id?: string }
  | { action: 'send_product'; customer_phone: string; customer_name?: string; product_id: string; crm_call_id?: string }
  | { action: 'send_text'; customer_phone: string; customer_name?: string; text: string; crm_call_id?: string };

export interface WaQuickActionResponse {
  ok: boolean;
  request_id: string;
  wa_message_id?: string;
  conversation_id?: string;
  errors?: string[];
}

export function useAgentWaQuickActions() {
  const { tenantId } = useAuth();

  return useMutation({
    mutationFn: async (input: WaQuickActionInput): Promise<WaQuickActionResponse> => {
      if (!tenantId) throw new Error('tenant_id required');

      const { data: session } = await supabase.auth.getSession();
      const accessToken = session?.session?.access_token;
      if (!accessToken) throw new Error('No active session');

      const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl;
      const url = `${supabaseUrl}/functions/v1/wa-quick-actions`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ tenant_id: tenantId, ...input }),
      });

      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        throw new Error(`wa-quick-actions failed: ${res.status} ${errBody.slice(0, 200)}`);
      }

      return await res.json() as WaQuickActionResponse;
    },
  });
}
